import {
  createStyles,
  withStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  ConfirmationDialog
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, DialogProps } from '@paintscout/ui'
import type { WithClientOptions, WithDialogStackContext } from '@paintscout/ui-business'
import { withDialogStackContext, withClientOptions } from '@paintscout/ui-business'
import CloseIcon from '@material-ui/icons/Close'
import type { EditableRate } from '@paintscout/util/builder'
import React from 'react'
import type { QuoteDocument } from 'paintscout'
import RateSelector from './RateSelector'

export interface RateSelectDialogProps extends DialogProps, WithDialogStackContext, WithClientOptions {
  classes?: DialogProps['classes'] & StyleClasses<typeof styles>
  onConfirm?: (rateOption: EditableRate, fields?: unknown) => void
  onCancel?: () => void
  quoteType?: string
  section?: string
  quote: QuoteDocument
  checkParentHasChanges?: () => boolean
}

export interface RateSelectDialogState {
  rate?: EditableRate
  fields?: unknown
  section?: string
  triggerBackToCategories?: number
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    dialogContent: {
      paddingTop: 0,
      [theme.breakpoints.down('sm')]: {
        padding: 0
      }
    },
    contentInner: {
      padding: `0 ${theme.spacing(0)}px`,
      [theme.breakpoints.down('sm')]: {
        padding: `0 ${theme.spacing(3)}px`
      }
    }
  })
}

class RateSelectDialog extends React.PureComponent<RateSelectDialogProps, RateSelectDialogState> {
  private isViewingCategories = true

  constructor(props: RateSelectDialogProps) {
    super(props)

    this.state = {
      rate: null,
      fields: {},
      triggerBackToCategories: 0
    }
    // Initialize based on whether a section is provided
    this.isViewingCategories = !props.section
  }

  public render() {
    const {
      classes,
      quote,
      quoteType,
      section,
      onCancel: _onCancel,
      onConfirm: _onConfirm,
      dialogStackContext,
      clientOptions,
      ...otherProps
    } = this.props

    const { options, rates } = clientOptions

    const leftButton = (
      <Button onClick={this.handleBack} variant="textNoPadding">
        Back
      </Button>
    )

    return (
      <Dialog {...otherProps} className={classes.root} maxWidth={'md'} fullWidth={true}>
        <DialogTitle
          rightContent={
            <IconButton size="small" onClick={this.handleClose} disabled={otherProps.loading}>
              <CloseIcon />
            </IconButton>
          }
        >
          Select Rate
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <div className={classes.contentInner}>
            <RateSelector
              quote={quote}
              dialogStackContext={dialogStackContext}
              options={options}
              rates={rates}
              rateSection={section}
              quoteType={quoteType}
              onChange={this.handleChange}
              onComplete={this.handleConfirm}
              onViewStateChange={this.handleViewStateChange}
              triggerBackToCategories={this.state.triggerBackToCategories}
            />
          </div>
        </DialogContent>
        <DialogActions leftButton={leftButton}>
          {this.state.rate &&
            (this.state.rate.rateType === 'quantity' ||
              this.state.rate.calculationType === 'hours' ||
              this.state.rate.calculationType === 'price') && (
              <Button
                onClick={() => {
                  setTimeout(this.handleConfirm, 0)
                }}
                variant={'contained'}
              >
                Done
              </Button>
            )}
        </DialogActions>
      </Dialog>
    )
  }

  public handleChange = (event: any, rate: EditableRate, fields?: unknown) => {
    if (rate.rateType !== 'quantity' && rate.calculationType !== 'hours' && rate.calculationType !== 'price') {
      if (this.props.onConfirm) {
        this.props.onConfirm(rate)
      }
    } else {
      this.setState({
        rate,
        fields
      })
    }
  }

  public handleConfirm = (_event: any) => {
    const { rate, fields } = this.state

    if (this.props.onConfirm) {
      this.props.onConfirm(rate, fields)
    }
  }

  public handleViewStateChange = (viewingCategories: boolean) => {
    this.isViewingCategories = viewingCategories
  }

  public handleBack = () => {
    // If we're viewing rates, go back to categories
    // If we're viewing categories, go back to the previous dialog (Area Selection)
    if (!this.isViewingCategories) {
      // Trigger going back to categories by incrementing the trigger prop
      this.setState({
        rate: null,
        fields: {},
        triggerBackToCategories: (this.state.triggerBackToCategories || 0) + 1
      })
    } else {
      // We're viewing categories, so go back to Area Selection
      const { onCancel } = this.props
      if (onCancel) {
        onCancel()
      }
    }
  }

  public handleClose = () => {
    const { dialogStackContext, checkParentHasChanges } = this.props
    const { rate, fields } = this.state
    const hasLocalChanges = rate !== null || (fields && Object.keys(fields).length > 0)
    const hasParentChanges = checkParentHasChanges ? checkParentHasChanges() : false
    const hasChanges = hasLocalChanges || hasParentChanges

    if (hasChanges) {
      dialogStackContext.openDialog(ConfirmationDialog, {
        message: 'You have unsaved changes. Are you sure you want to close?',
        onConfirm: () => {
          dialogStackContext.dismissDialog() // Dismiss confirmation dialog
          dialogStackContext.dismissAllDialogs() // Close all dialogs
        },
        onCancel: () => {
          dialogStackContext.dismissDialog()
        }
      })
    } else {
      dialogStackContext.dismissAllDialogs() // Close all dialogs when no changes
    }
  }
}

export default withStyles(styles)(withDialogStackContext(withClientOptions(RateSelectDialog)))
